import { Queue } from '../queue/queue.js';
/**
 * Creates a function that limits the number of concurrent executions of async functions.
 *
 * @param maxConcurrent - Maximum number of concurrent executions allowed
 * @returns A function that wraps async functions to limit their concurrent execution
 *
 * @example
 * ```ts
 * const limiter = createLimiter(2) // Allow max 2 concurrent executions
 *
 * // These will run with max 2 at a time
 * const results = await Promise.all([
 *   limiter(() => fetch('/api/1')),
 *   limiter(() => fetch('/api/2')),
 *   limiter(() => fetch('/api/3')),
 *   limiter(() => fetch('/api/4'))
 * ])
 * ```
 */
export function createLimiter(maxConcurrent) {
    let activeCount = 0;
    const queue = new Queue();
    const next = () => {
        if (queue.isEmpty() || activeCount >= maxConcurrent) {
            return;
        }
        const resolve = queue.dequeue();
        if (resolve) {
            resolve();
        }
    };
    const run = async (fn) => {
        if (activeCount >= maxConcurrent) {
            await new Promise((resolve) => queue.enqueue(resolve));
        }
        activeCount++;
        try {
            const result = await fn();
            return result;
        }
        finally {
            activeCount--;
            next();
        }
    };
    return run;
}
